import { KeyboardArrowDown } from '@mui/icons-material';
import { Autocomplete, createTheme, TextField, ThemeProvider, Typography } from '@mui/material';
import { convertToPST } from 'app/helpers/dateHelper';
import { IRequirementRowPadding } from 'app/models/Placement/PlacementSummary';
import { selectUser } from 'oidc/user.selectors';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { theme } from 'styles/global-styles';
import { Concatenate } from 'utils/string/string';
import {
  selectEditableFields,
  selectEditableRequirementTypes,
  selectOfferInfo,
} from '../../../../../../../../../store/redux-store/placement-summary/selectors';
import { useRequirementFilter } from '../../../Helpers/requirementFilter';
import { ConfirmationsRow } from '../../TableComponents/Confirmations/ConfirmationsTable';
import { useArrivedFieldsetMeta } from '../useArrivedFieldsetMeta';
import { CustomTooltip } from '@AMIEWEB/Common';

export const DnsCleared = ({ hasRequirements }: IRequirementRowPadding) => {
  const { t } = useTranslation();
  const { dnsCleared: initialValue } = useSelector(selectEditableFields);
  const { requirementFilter } = useSelector(selectOfferInfo);
  const { dnsCleared } = useSelector(selectEditableRequirementTypes);
  const { control } = useFormContext();
  const { userInfo } = useSelector(selectUser);
  const arrivedFieldsetMeta = useArrivedFieldsetMeta('eligibility');

  const { display } = useRequirementFilter('offer-confirmation', requirementFilter, dnsCleared?.requirement);
  const subTheme = createTheme(theme, {
    components: {
      MuiInputBase: {
        styleOverrides: {
          root: {
            background: '#fff !important',
          },
        },
      },
    },
  });

  const eligibilityCleared = {
    Pass: 'Pass',
    Fail: 'Fail',
  };

  const getSelectedValue = (checked: boolean | null | undefined) =>
    checked === null || checked === undefined ? null : checked ? eligibilityCleared.Pass : eligibilityCleared.Fail;

  return display ? (
    <Controller
      control={control}
      name="dnsCleared"
      render={({ field: { onChange, value } }) => (
        <ConfirmationsRow
          id="placement-offer-confirmations-dns-cleared"
          lastUpdatedBy={value?.lastUpdatedBy}
          lastUpdatedDate={value?.lastUpdatedAt}
          requirement={dnsCleared?.requirement}
          hasOtherRequirements={hasRequirements}
          controlComponent={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{ width: 'max-content' }} component={'span'} variant="body1">
                {t('placement.profile.summary.offer.confirmations.eligibilityCleared')}
              </Typography>
              <CustomTooltip disabled={!arrivedFieldsetMeta.disabled} tooltipContent={arrivedFieldsetMeta.title} placement="bottom-start" arrow={false} isStandardContent>
                <ThemeProvider theme={subTheme}>
                  <Autocomplete
                    sx={{ width: '74px' }}
                    options={[eligibilityCleared.Pass, eligibilityCleared.Fail]}
                    value={getSelectedValue(value?.checked)}
                    disableClearable
                    disabled={arrivedFieldsetMeta.disabled}
                    popupIcon={<KeyboardArrowDown />}
                    onChange={(e, newValue) => {
                      const newValueIsChecked = newValue === eligibilityCleared.Pass;
                      if (initialValue?.checked === newValueIsChecked) {
                        onChange?.({ ...initialValue });
                      } else {
                        onChange?.({
                          checked: newValue === eligibilityCleared.Pass,
                          lastUpdatedBy: Concatenate([userInfo?.firstName || '', userInfo?.lastName || ''], ' '),
                          lastUpdatedById: userInfo?.employeeId || null,
                          lastUpdatedAt: convertToPST(new Date(), 'yyyy-MM-DDTHH:mm:ss'),
                        });
                      }
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="select"
                        variant="standard"
                        sx={{
                          input: {
                            '&::placeholder': {
                              fontStyle: 'italic',
                            },
                          },
                        }}
                      />
                    )}
                  />
                </ThemeProvider>
              </CustomTooltip>
            </div>
          }
        />
      )}
    />
  ) : null;
};
